import { Merchandise, MerchandiseCatalogItem } from "types";

export interface VariantCatalogItem extends MerchandiseCatalogItem {
    parentId?: string | null;
    variantGroupId?: string;
    variantOptionId?: string;
    variantOptionName?: string;
    isLeaf?: boolean;
    depth?: number;
}

interface CatalogItemWithSelectedVariants extends VariantCatalogItem {
    selectedVariants?: string[];
    variantGroupOptions?: string[];
}

const getSelectedVariantOptionIds = (item: CatalogItemWithSelectedVariants): string[] => {
    return item.selectedVariants ?? item.variantGroupOptions ?? [];
};

export const buildVariantTree = (items: VariantCatalogItem[]): Map<string | null, VariantCatalogItem[]> => {
    const tree = new Map<string | null, VariantCatalogItem[]>();

    for (const item of items) {
        const parentId = item.parentId ?? null;
        if (!tree.has(parentId)) {
            tree.set(parentId, []);
        }
        tree.get(parentId)?.push(item);
    }

    return tree;
};

export const getChildren = (
    tree: Map<string | null, VariantCatalogItem[]>,
    parentId: string | null,
): VariantCatalogItem[] => {
    return tree.get(parentId) ?? [];
};

export const getVariantNodeId = (parentId: string | null, optionId: string) => {
    return `${parentId ?? "root"}:${optionId}`;
};

export const getVariantCatalogItems = (
    merchandise: Merchandise,
    fetchedCatalogItems?: MerchandiseCatalogItem[],
): VariantCatalogItem[] => {
    const groups = [...(merchandise.variantGroups ?? [])].sort(
        (first, second) => (first.sequence ?? 0) - (second.sequence ?? 0),
    );

    const optionToGroup = new Map(
        groups.flatMap((group, groupIndex) =>
            group.options.map(
                (option, optionIndex) =>
                    [
                        option.id ?? `${groupIndex}-${optionIndex}`,
                        { groupId: group.id ?? "", groupIndex, name: option.name },
                    ] as const,
            ),
        ),
    );

    const sortByGroupOrder = (optionIds: string[]): string[] => {
        return [...optionIds].sort((first, second) => {
            const firstOrder = optionToGroup.get(first)?.groupIndex ?? Number.MAX_SAFE_INTEGER;
            const secondOrder = optionToGroup.get(second)?.groupIndex ?? Number.MAX_SAFE_INTEGER;
            return firstOrder - secondOrder;
        });
    };

    const catalogItems = (fetchedCatalogItems ?? merchandise.catalog?.items ?? []) as CatalogItemWithSelectedVariants[];
    const branchNodes = new Map<string, VariantCatalogItem>();

    for (const item of catalogItems) {
        const selectedVariants = sortByGroupOrder(getSelectedVariantOptionIds(item));
        const branchVariants = selectedVariants.slice(0, -1);

        let parentId: string | null = null;
        let depth = 0;

        for (const optionId of branchVariants) {
            const currentNodeId = getVariantNodeId(parentId, optionId);
            if (branchNodes.has(currentNodeId)) {
                parentId = currentNodeId;
                depth += 1;
                continue;
            }

            const optionInfo = optionToGroup.get(optionId);
            const variantOptionName = optionInfo?.name ?? item.name ?? "";

            branchNodes.set(currentNodeId, {
                id: currentNodeId,
                object: "ware_catalog_item",
                name: variantOptionName,
                parentId,
                variantGroupId: optionInfo?.groupId,
                variantOptionId: optionId,
                variantOptionName,
                isLeaf: false,
                depth,
            } as VariantCatalogItem);

            parentId = currentNodeId;
            depth += 1;
        }
    }

    const leafNodes = catalogItems.map((item) => {
        const selectedVariants = sortByGroupOrder(getSelectedVariantOptionIds(item));
        const branchVariants = selectedVariants.slice(0, -1);

        let parentId: string | null = null;
        for (const optionId of branchVariants) {
            parentId = getVariantNodeId(parentId, optionId);
        }

        const lastOptionId = selectedVariants[selectedVariants.length - 1];
        const lastOptionInfo = lastOptionId ? optionToGroup.get(lastOptionId) : undefined;
        const variantOptionName = lastOptionInfo?.name ?? item.name ?? "";

        return {
            ...item,
            parentId,
            variantGroupId: lastOptionInfo?.groupId,
            variantOptionId: lastOptionId,
            variantOptionName,
            isLeaf: true,
            depth: selectedVariants.length,
        } as VariantCatalogItem;
    });

    return [...branchNodes.values(), ...leafNodes];
};

export const getVariantPath = (item: VariantCatalogItem, catalogItems: VariantCatalogItem[]): string => {
    const path: string[] = [];
    let currentItem: VariantCatalogItem | undefined = item;

    while (currentItem) {
        path.unshift(currentItem.variantOptionName ?? currentItem.name ?? "");
        const parentId: string | null | undefined = currentItem.parentId;
        currentItem = parentId ? catalogItems.find((index) => index.id === parentId) : undefined;
    }

    return path.join(" / ");
};

export const getLeafDescendants = (nodeId: string, tree: Map<string | null, VariantCatalogItem[]>): string[] => {
    const children = getChildren(tree, nodeId);
    const result: string[] = [];
    for (const child of children) {
        if (!child.id) continue;
        if (child.isLeaf) {
            result.push(child.id);
        } else {
            result.push(...getLeafDescendants(child.id, tree));
        }
    }
    return result;
};
